import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { useLang } from "@/i18n";

const COPY = {
  it: {
    badge: "Sito Essenziale",
    deskL1: "Hai bisogno di un sito essenziale?",
    deskL2: "Perimetro definito. Prezzo chiaro.",
    mobL1: "Ti serve un sito?",
    mobL2: "Perimetro definito.",
    likeThis: "Una soluzione web professionale con un perimetro definito.",
    price: "€1.200",
    oneDay: "+ IVA · Analisi, contenuti, design e pubblicazione inclusi.",
    oneDayMob: "+ IVA, tutto incluso nel perimetro.",
    body: "Analisi, struttura, contenuti, copy essenziale, design responsive fino a 5 pagine, form contatti, SEO di base e pubblicazione.",
    list: ["Fino a 5 pagine", "Design professionale", "Responsive", "Copy essenziale e SEO base", "Form contatti e pubblicazione"],
    listMob: ["Fino a 5 pagine", "Responsive", "Design professionale", "Form contatti"],
    cta: "Scopri il Sito Essenziale",
    ctaMob: "Scopri l'offerta",
    ctaSecondary: "Parliamone",
    note: "Un perimetro definito a un prezzo chiaro. Se il progetto cresce, le attività aggiuntive vengono valutate separatamente.",
    close: "Chiudi",
  },
  en: {
    badge: "Essential Website",
    deskL1: "Need an essential website?",
    deskL2: "Defined scope. Clear price.",
    mobL1: "Need a website?",
    mobL2: "Defined scope.",
    likeThis: "A professional web solution with a defined scope.",
    price: "€1,200",
    oneDay: "+ VAT · Analysis, content, design and publication included.",
    oneDayMob: "+ VAT, everything included in the scope.",
    body: "Analysis, structure, content, essential copy, responsive design up to 5 pages, contact form, basic SEO and publication.",
    list: ["Up to 5 pages", "Professional design", "Responsive", "Essential copy and basic SEO", "Contact form and publication"],
    listMob: ["Up to 5 pages", "Responsive", "Professional design", "Contact form"],
    cta: "Discover the Essential Website",
    ctaMob: "Discover the offer",
    ctaSecondary: "Let's talk",
    note: "A defined scope at a clear price. If the project grows, additional activities are quoted separately.",
    close: "Close",
  },
};

const PromoPopup = () => {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.it;
  const suppressed = ["/contatti", "/pagamento", "/sito-essenziale"].some((p) => location.pathname.startsWith(p)) || location.pathname.startsWith("/admin");

  useEffect(() => {
    if (suppressed) return;
    if (sessionStorage.getItem("mf_promo_closed")) return;
    const timer = setTimeout(() => setShow(true), 6000);
    const onExit = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) {
        setShow(true);
        document.removeEventListener("mouseout", onExit);
      }
    };
    document.addEventListener("mouseout", onExit);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onExit);
    };
  }, [suppressed]);

  const close = () => {
    setShow(false);
    try {
      sessionStorage.setItem("mf_promo_closed", "1");
    } catch {}
  };

  return (
    <AnimatePresence>
      {show && !suppressed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/70 p-4 backdrop-blur-sm"
          data-testid="promo-popup"
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative grid max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-md bg-ink text-paper lg:grid-cols-2"
          >
            <button
              type="button"
              onClick={close}
              data-testid="promo-close"
              aria-label={c.close}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-md border border-white/25 bg-ink/60 text-white transition-colors hover:border-accent hover:bg-accent"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="p-8 sm:p-10 lg:p-12">
              <span className="inline-block rounded-sm bg-accent px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-white" data-testid="promo-badge">
                {c.badge}
              </span>
              <h2 className="mt-6 hidden font-serif text-4xl font-light leading-[1.05] tracking-tight lg:block">
                {c.deskL1}
                <br />
                <span className="italic text-accent-bright">{c.deskL2}</span>
              </h2>
              <h2 className="mt-6 font-serif text-3xl font-light leading-[1.08] tracking-tight lg:hidden">
                {c.mobL1}
                <br />
                <span className="italic text-accent-bright">{c.mobL2}</span>
              </h2>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">{c.likeThis}</p>
              <div className="mt-2 flex items-end gap-4">
                <p className="font-serif text-6xl font-light leading-none tracking-tight text-accent-bright sm:text-7xl" data-testid="promo-price">
                  {c.price}
                </p>
                <p className="pb-2 text-sm font-medium leading-snug text-white/80">
                  <span className="hidden lg:inline">{c.oneDay}</span>
                  <span className="lg:hidden">{c.oneDayMob}</span>
                </p>
              </div>
              <p className="mt-5 hidden text-sm leading-relaxed text-white/60 lg:block">{c.body}</p>
              <ul className="mt-6 space-y-2">
                {c.list.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-white/85">
                    <Check className="h-4 w-4 shrink-0 text-accent-bright" strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/sito-essenziale"
                onClick={close}
                data-testid="promo-cta-primary"
                className="mt-8 block rounded-md bg-accent px-8 py-4 text-center text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-accent-dark"
              >
                <span className="hidden lg:inline">{c.cta}</span>
                <span className="lg:hidden">{c.ctaMob}</span>
              </Link>
              <div className="mt-3 flex items-center justify-between gap-4">
                <Link
                  to="/contatti"
                  onClick={close}
                  data-testid="promo-cta-secondary"
                  className="text-xs font-semibold uppercase tracking-[0.15em] text-white/70 underline underline-offset-4 transition-colors hover:text-accent-bright"
                >
                  {c.ctaSecondary}
                </Link>
                <button type="button" onClick={close} data-testid="promo-close-text" className="text-xs uppercase tracking-[0.15em] text-white/40 transition-colors hover:text-white lg:hidden">
                  {c.close}
                </button>
              </div>
              <p className="mt-6 border-t border-white/10 pt-4 text-[11px] leading-relaxed text-white/40">{c.note}</p>
            </div>

            <div className="relative hidden min-h-full lg:block">
              <img src="/images/site-mockup.png" alt="Marketing Facile — anteprima del sito" className="absolute inset-0 h-full w-full object-cover" data-testid="promo-image" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/20 to-transparent" />
              <span className="absolute bottom-6 right-6 rotate-3 rounded-sm bg-paper px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-ink">
                Sito Essenziale
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromoPopup;
